import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import { Trash2, Plus, Box } from 'lucide-react';

const ModelManager = () => {
    const [models, setModels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isAdding, setIsAdding] = useState(false);

    // New Model State
    const [newModel, setNewModel] = useState({
        name: '',
        modelFile: null,
        thumbnailFile: null,
        scale: { x: 1, y: 1, z: 1 },
        rotation: { x: 0, y: 0, z: 0 },
        position: { x: 0, y: 0, z: 0 },
        material: { color: '#ffffff', metalness: 0.5, roughness: 0.5 },
    });

    const fetchModels = async () => {
        try {
            const { data } = await api.get('/models');
            setModels(data);
            setLoading(false);
        } catch (error) {
            console.error(error);
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchModels();
    }, []);

    const handleInputChange = (e, section = null) => {
        const { name, value } = e.target;
        if (section) {
            setNewModel(prev => ({
                ...prev,
                [section]: { ...prev[section], [name]: parseFloat(value) }
            }));
        } else {
            setNewModel(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleFileChange = (e) => {
        const { name, files } = e.target;
        setNewModel(prev => ({ ...prev, [name]: files[0] }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const formData = new FormData();
        formData.append('name', newModel.name);
        // Append files if they exist
        if (newModel.modelFile) formData.append('modelFile', newModel.modelFile);
        if (newModel.thumbnailFile) formData.append('thumbnailFile', newModel.thumbnailFile);

        formData.append('scale', JSON.stringify(newModel.scale));
        formData.append('rotation', JSON.stringify(newModel.rotation));
        formData.append('position', JSON.stringify(newModel.position));
        formData.append('material', JSON.stringify(newModel.material));

        try {
            await api.post('/models', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            setIsAdding(false);
            fetchModels(); // Refresh list
            // Reset form (simplified)
            setNewModel({
                name: '',
                modelFile: null,
                thumbnailFile: null,
                scale: { x: 1, y: 1, z: 1 },
                rotation: { x: 0, y: 0, z: 0 },
                position: { x: 0, y: 0, z: 0 },
                material: { color: '#ffffff', metalness: 0.5, roughness: 0.5 },
            });
        } catch (error) {
            console.error('Error adding model', error);
            alert('Failed to add model. See console.');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this model?')) {
            try {
                await api.delete(`/models/${id}`);
                fetchModels();
            } catch (error) {
                console.error(error);
            }
        }
    };

    if (loading) return <div>Loading models...</div>;

    return (
        <div className="max-w-6xl mx-auto">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold">3D Model Manager</h1>
                <button
                    onClick={() => setIsAdding(!isAdding)}
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                    <Plus className="w-4 h-4 mr-2" />
                    Add New Model
                </button>
            </div>

            {isAdding && (
                <div className="bg-white p-6 rounded shadow mb-8 border transition-all">
                    <h2 className="text-xl font-semibold mb-4">Add New Model</h2>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium">Model Name</label>
                                <input type="text" name="name" onChange={handleInputChange} className="w-full border p-2 rounded" required />
                            </div>

                            <div>
                                <label className="block text-sm font-medium">Model File (.glb/.gltf)</label>
                                <input type="file" name="modelFile" onChange={handleFileChange} className="w-full border p-2 rounded" accept=".glb,.gltf" />
                            </div>

                            <div>
                                <label className="block text-sm font-medium">Thumbnail</label>
                                <input type="file" name="thumbnailFile" onChange={handleFileChange} className="w-full border p-2 rounded" accept="image/*" />
                            </div>
                        </div>

                        {/* Transformations */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-t pt-4">
                            <div>
                                <h3 className="font-medium mb-2">Scale</h3>
                                <div className="flex space-x-2">
                                    <input type="number" placeholder="X" name="x" step="0.1" onChange={(e) => handleInputChange(e, 'scale')} className="w-full border p-1" />
                                    <input type="number" placeholder="Y" name="y" step="0.1" onChange={(e) => handleInputChange(e, 'scale')} className="w-full border p-1" />
                                    <input type="number" placeholder="Z" name="z" step="0.1" onChange={(e) => handleInputChange(e, 'scale')} className="w-full border p-1" />
                                </div>
                            </div>
                            {/* Add Rotation and Position similarly if needed, keeping it simple for now */}
                        </div>

                        <div className="flex justify-end gap-2 mt-4">
                            <button type="button" onClick={() => setIsAdding(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded">Cancel</button>
                            <button type="submit" className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800">Upload & Save</button>
                        </div>
                    </form>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {models.map((model) => (
                    <div key={model._id} className="bg-white rounded shadow p-4 flex flex-col items-center relative group">
                        <div className="w-full h-40 bg-gray-100 flex items-center justify-center rounded mb-4 overflow-hidden">
                            {model.thumbnail ? (
                                <img src={`${import.meta.env.VITE_API_URL.replace('/api', '')}${model.thumbnail}`} alt={model.name} className="w-full h-full object-cover" />
                            ) : (
                                <Box className="w-12 h-12 text-gray-400" />
                            )}
                        </div>
                        <h3 className="font-semibold text-lg">{model.name}</h3>
                        <p className="text-xs text-gray-500 mb-2">ID: {model._id}</p>

                        <div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <a
                                href={`/admin/models/edit/${model._id}`}
                                className="p-2 bg-blue-100 text-blue-600 rounded-full hover:bg-blue-200"
                                onClick={(e) => { e.preventDefault(); window.location.href = `/admin/models/edit/${model._id}`; }}
                            >
                                <Box className="w-4 h-4" />
                            </a>
                            <button
                                onClick={() => handleDelete(model._id)}
                                className="p-2 bg-red-100 text-red-600 rounded-full hover:bg-red-200"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                ))}
                {models.length === 0 && !loading && (
                    <p className="text-gray-500 col-span-full text-center">No models found. Add one to get started.</p>
                )}
            </div>
        </div>
    );
};

export default ModelManager;
